import { z } from "zod";

/** Search everything (M2b): the words of a query, the snippet around the first word found (ported from the prototype's
 *  lib/search.ts), and the view a ticket lives in for a hit's link. The API applies the matching rule in SQL. */
export const SearchQuerySchema = z.object({ q: z.string().max(200).default("") });
export const SEARCH_MAX_WORDS = 8;
/** case and accents folded */
export const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
/** the words that count: at least two characters, the search operators gone, the first eight; a query under two characters is none */
export function searchWords(q: string): string[] {
  const clean = q.replace(/[&|!:()'*<>\\"]/g, " ").trim();
  if (clean.length < 2) return [];
  return clean.split(/\s+/).map((w) => w.replace(/^#/, "")).filter((w) => w.length >= 2).slice(0, SEARCH_MAX_WORDS);
}
/** one word as a prefix for Postgres' `to_tsquery` (case and accents folded, its operators gone): "hrefl" finds "hreflang". The ticket
 *  search and team chat's search in a room (M10 round two) both match words this way. */
export const prefixTsQuery = (w: string) => `${fold(w).replace(/[&|!:()'*<>\\]/g, "")}:*`;
/** About seventy characters around the first word found, with an ellipsis where text was cut. */
export function snippetOf(text: string, word: string, span = 70): string {
  const flat = text.replace(/\s+/g, " ").trim();
  const first = fold(word).trim();
  const at = first ? fold(flat).indexOf(first) : -1;
  if (at < 0 || flat.length <= span * 2) return flat.slice(0, span * 2) + (flat.length > span * 2 ? "…" : "");
  const from = Math.max(0, at - span / 2), to = Math.min(flat.length, at + span * 1.5);
  return `${from > 0 ? "…" : ""}${flat.slice(from, to).trim()}${to < flat.length ? "…" : ""}`;
}
export const SEARCH_SECTIONS = [{ id: "tickets", label: "Tickets" }, { id: "customers", label: "Customers" }] as const;
export type SearchHit = { id: string; title: string; snippet: string; to: string; meta: string };
export type SearchResults = { tickets: SearchHit[]; customers: SearchHit[]; total: number };
/** the list a ticket lives in, for a link that must open it somewhere */
export const viewOfTicket = (t: { status: "open" | "pending" | "resolved"; spam: boolean }) => (t.spam ? "spam" : t.status === "open" ? "all-open" : t.status === "pending" ? "awaiting" : "solved");
